import { useEffect } from 'react';

/**
 * Helpers for written content: the editor, the blog and the CMS pages.
 *
 * In their own module rather than beside the components that use them, because
 * React Fast Refresh cannot hot-swap a file that exports both a component and a
 * plain function — and losing HMR on the editor means a full reload after every
 * keystroke-sized change to it, which is the one screen where that hurts most.
 */

/**
 * Words, characters and minutes.
 *
 * 220 words a minute, rounded up, floored at one — character for character the
 * same arithmetic as `ContentPage::readingMinutesFor`. The two have to agree:
 * if they drift, the figure a writer sees while writing is not the figure
 * readers are shown, and nobody would ever notice which one was wrong.
 *
 * 220 is the middle of the measured range for adult reading of ordinary prose.
 * Rounding up and flooring at one because "0 min read" is absurd and because
 * under-promising is the safer error.
 */
export function countWords(source: string): {
  words: number;
  characters: number;
  minutes: number;
} {
  const words = source.trim() ? source.trim().split(/\s+/).length : 0;

  return {
    words,
    characters: source.length,
    minutes: Math.max(1, Math.ceil(words / 220)),
  };
}

/** A date, as a Nigerian reader would write it. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Warn before leaving with unsaved work.
 *
 * An article is twenty minutes of writing. A closed tab that takes it with it
 * is not a bug anybody can fix afterwards.
 */
export function useUnsavedGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browsers ignore the string and show their own wording. Setting it is
      // still what arms the dialog in some of them.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}
